import {
  ChangeDetectorRef,
  Component,
  EventEmitter, Input, OnChanges,
  OnInit,
  Output,
  SimpleChanges, ViewChild
} from '@angular/core';
import {CopyPasteService, IWithSpecs, SpecLineModel, WebUserModel} from 'web-shared';
import {
  BaseComponent, ClassificatorValueQuery,
  ClassificatorValuesQuery,
  GLOBAL, IdTextStateModel,
  LanguagesQuery, PropertiesQuery
} from 'common-shared';
import {combineLatest, fromEvent, merge, Subject, Subscription} from 'rxjs';
import {SpecModelHandlingService} from "web-shared";
import {take, takeUntil} from "rxjs/operators";
import {marker} from "@jsverse/transloco-keys-manager/marker";
import {StateService} from "web-shared";
import {instanceToPlain, plainToInstance} from 'class-transformer';
import {Table, TableCheckbox} from "primeng/table";
import {SpecLinesEditColumns, TableColumn} from "../../../core/table-column";
import {ResourceService} from "../../../manufacturing/resources/resource.service";

@Component({
  selector: 'ime-spec-lines-edit',
  templateUrl: './spec-lines-edit.component.html'
})
export class SpecLinesEditComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() context: IWithSpecs;
  @Input() showLocalization: boolean = false;
  @Input() showLocalizationButton: boolean = false;
  @Input() wasValidated$: Subject<boolean> = new Subject<boolean>();
  @Input() readonly: boolean;
  wasValidated: boolean = false;
  @Output() changed = new EventEmitter<SpecLineModel>();
  showLocalizationByLine: boolean = false;
  showCheckboxes: boolean = false;
  selectedSpecs: SpecLineModel[] = [];
  toggleableColumns: TableColumn<SpecLinesEditColumns>[] = [];
  columnIdentifiers = SpecLinesEditColumns;
  editableCellId: string;
  clickSubscription: Subscription;
  showOwnProperties = {Identifier: 99, IsSelected: false};
  @ViewChild(Table) table: Table;
  shouldSaveSettings: boolean = false;

  constructor(public propertiesQuery: PropertiesQuery, public classificatorValuesQuery: ClassificatorValuesQuery, public languagesQuery: LanguagesQuery, private cdRef: ChangeDetectorRef, public specModelHandlingService: SpecModelHandlingService, private resourceService: ResourceService, private stateService: StateService, public copyPasteService: CopyPasteService) {
    super();
  }

  ngOnInit() {
    this.wasValidated$.pipe(takeUntil(this.componentDestroyed)).subscribe(wasValidated => {
      this.scrollToSpec(this.context.Specs.find(spec => !spec.isValid())?.Id);
      this.wasValidated = wasValidated;
    });
    this.setColumnStates();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.showLocalization?.currentValue === false) {
      this.context.Specs.forEach(spec => spec.ShowLocalization = false);
      this.showLocalizationByLine = false;
    }
  }

  onPropertyChanged(line: SpecLineModel) {
    this.ensureShowLocalizationIsSet(line);
    this.ensureRedundantGroupClvRemoved(line);
    if (line.LineSource?.Id) {
      this.onLineChanged(line);
    }
    if (line.MappingSource?.Id) {
      this.onMappingChanged(line);
    }
    this.scrollToSpec(line.Id);
  }

  private ensureShowLocalizationIsSet(line: SpecLineModel) {
    const specLineModel = this.context.Specs.find(spec => spec.PropertyId === line.PropertyId && !spec.LanguageId);
    if (specLineModel) {
      line.ShowLocalization = specLineModel.ShowLocalization;
    }
  }

  private ensureRedundantGroupClvRemoved(line: SpecLineModel) {
    if (line.GroupClv) {
      this.propertiesQuery.selectEntity(line.PropertyId).pipe(takeUntil(this.componentDestroyed)).subscribe(property => {
        if (property?.GroupClv == line.GroupClv) {
          line.GroupClv = undefined;
        }
      });
    }
  }

  onLineChanged(line: SpecLineModel) {
    line.resetLineSource({Id: this.context.Id, Code: this.context.Code});
    this.changed.emit(line);
  }

  onMappingChanged(line: SpecLineModel) {
    let source = {Id: this.context.Id, Code: this.context.Code};
    for (const specLineModel of this.context.Specs.filter(value => value.PropertyId === line.PropertyId && !!value.LanguageId)) {
      specLineModel.MandatoryClv = line.MandatoryClv;
      specLineModel.resetMappingSource(source);
      this.changed.emit(specLineModel);
    }

    line.resetMappingSource(source);
    this.changed.emit(line);
  }

  onClvPropertyChanged(line: SpecLineModel, clv: string, propertyName: string) {
    this.onMappingChanged(line);
    line[propertyName] = clv;
    this.context.Specs = [...this.context.Specs];
    this.scrollToSpec(line.Id);
  }

  canDelete(line: SpecLineModel) {
    //TODO: Source compare?
    return true;
  }

  addNewSpecLine(rowGroupKey: string = undefined) {
    combineLatest([this.classificatorValuesQuery.mandatories$, this.classificatorValuesQuery.mappingOrigins$])
      .pipe(take(1)).subscribe(([mandatories, origins]) => {
      const groupClv = rowGroupKey?.split('|').pop() ?? undefined;
      const specLineModel = new SpecLineModel({
        MandatoryClv: mandatories?.find(m => m.IsDefault)?.Id,
        OriginClv: origins?.find(m => m.IsDefault)?.Id,
        GroupClv: groupClv,
        $isNew: true,
        RowGroupKey: rowGroupKey,
      });
      this.onLineChanged(specLineModel);
      this.context.Specs = [...this.context.Specs, specLineModel];
      this.scrollToSpec(specLineModel.Id);
      this.cdRef.detectChanges();
    });
  }

  remove(spec: SpecLineModel) {
    this.context.Specs = [...this.context.Specs.filter(value => value.Id !== spec.Id)];
    this.changed.emit();
  }

  removeSelected() {
    this.context.Specs = [...this.context.Specs.filter(spec => !this.selectedSpecs.includes(spec))];
    this.selectedSpecs = [];
    this.changed.emit();
  }

  scrollToSpec(id: string): void {
    setTimeout(() => {
      const element = document.getElementById('property-' + id);
      if (element) {
        element.scrollIntoView({behavior: 'smooth', block: "center"});
      }
    }, 200);
  }

  setShowLocalizationByLine(line: SpecLineModel): void {
    this.context.Specs.forEach(spec => {
      if (spec.PropertyId === line.PropertyId) {
        spec.ShowLocalization = !spec.ShowLocalization;
      }
    });
    this.showLocalizationByLine = this.context.Specs.some(spec => spec.ShowLocalization === true);
    this.context.Specs = [...this.context.Specs];
  }

  setColumnStates() {
    combineLatest([this.stateService.currentUser$, this.classificatorValuesQuery.mappingOrigins$])
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(([currentUser, origins]) => {
        if (currentUser == null || origins.length == 0) {
          return;
        }
        let toggleableColumns = this.getToggleableColumns(origins);
        const columnStates = this.getColumnStates(currentUser);
        if (columnStates) {
          columnStates.forEach(columnState => {
            let toggleableColumn = toggleableColumns.find(col => col.Identifier === columnState.Identifier);
            if (toggleableColumn) {
              toggleableColumn.IsSelected = columnState.IsSelected;
              if (toggleableColumn.Values?.length > 0) {
                for (let value of toggleableColumn.Values) {
                  let savedState = columnState.Values?.find(value1 => value1.Id == value.Id);
                  if (savedState != null) {
                    value.State = savedState.State;
                  }
                }
              }
            }

            if (columnState.Identifier === this.showOwnProperties.Identifier) {
              this.showOwnProperties.IsSelected = columnState.IsSelected;
            }
          });
        }
        this.toggleableColumns = toggleableColumns;
      });
  }

  toggleColumn(field: TableColumn<SpecLinesEditColumns>) {
    field.IsSelected = !field.IsSelected;
    this.toggleableColumns = [...this.toggleableColumns];
  }

  toggleColumnValue(columnValue: IdTextStateModel) {
    columnValue.State = !columnValue.State;
    this.toggleableColumns = [...this.toggleableColumns];
  }

  getToggleableColumns(origins: ClassificatorValueQuery[]): TableColumn<SpecLinesEditColumns>[] {
    const data: TableColumn<SpecLinesEditColumns>[] = [
      {
        Identifier: SpecLinesEditColumns.Source,
        Name: marker('ime.common.specs.Source'),
        Code: SpecLinesEditColumns[SpecLinesEditColumns.Source],
        Width: 80,
        IsSelected: false
      },
      {
        Identifier: SpecLinesEditColumns.Origin,
        Name: marker('ime.common.specs.Origin'),
        Code: SpecLinesEditColumns[SpecLinesEditColumns.Origin],
        Width: 96,
        IsSelected: false,
        Values: [...origins.map(value => {
          return {Id: value.Id, Text: value.Value, State: true}
        })]
      },
      {
        Identifier: SpecLinesEditColumns.Formula,
        Name: marker('ime.common.specs.Formula'),
        Code: SpecLinesEditColumns[SpecLinesEditColumns.Formula],
        Width: 384,
        IsSelected: true
      },
      {
        Identifier: SpecLinesEditColumns.Constraints,
        Name: marker('ime.common.specs.Constraints'),
        Code: SpecLinesEditColumns[SpecLinesEditColumns.Constraints],
        Width: 256,
        IsSelected: true
      },
      {
        Identifier: SpecLinesEditColumns.Mandatory,
        Name: marker('ime.common.specs.Visibility Formula'),
        Code: SpecLinesEditColumns[SpecLinesEditColumns.Mandatory],
        Width: 256,
        IsSelected: false
      },
      {
        Identifier: SpecLinesEditColumns.Alias,
        Name: marker('ime.common.specs.Alias'),
        Code: SpecLinesEditColumns[SpecLinesEditColumns.Alias],
        Width: 96,
        IsSelected: true
      },
      {
        Identifier: SpecLinesEditColumns.AliasFormula,
        Name: marker('ime.common.specs.Alias Formula'),
        Code: SpecLinesEditColumns[SpecLinesEditColumns.AliasFormula],
        Width: 256,
        IsSelected: false
      },
      {
        Identifier: SpecLinesEditColumns.Sequence,
        Name: marker('ime.common.specs.Sequence'),
        Code: SpecLinesEditColumns[SpecLinesEditColumns.Sequence],
        Width: 80,
        IsSelected: true
      },
      {
        Identifier: SpecLinesEditColumns.Group,
        Name: marker('ime.common.specs.Group'),
        Code: SpecLinesEditColumns[SpecLinesEditColumns.Group],
        Width: 112,
        IsSelected: true
      },
      {
        Identifier: SpecLinesEditColumns.Priority,
        Name: marker('ime.common.specs.Priority'),
        Code: SpecLinesEditColumns[SpecLinesEditColumns.Priority],
        Width: 96,
        IsSelected: false
      },
      {
        Identifier: SpecLinesEditColumns.Message,
        Name: marker('ime.common.specs.Message'),
        Code: SpecLinesEditColumns[SpecLinesEditColumns.Message],
        Width: 256,
        IsSelected: false
      },
      {
        Identifier: SpecLinesEditColumns.MessageFormula,
        Name: marker('ime.common.specs.Message Formula'),
        Code: SpecLinesEditColumns[SpecLinesEditColumns.MessageFormula],
        Width: 256,
        IsSelected: false
      },
    ];
    return plainToInstance(TableColumn, data) as unknown as TableColumn<SpecLinesEditColumns>[];
  }

  updateSettings(): void {
    let currentUser = this.stateService.currentUser$.getValue();
    if (this.shouldSaveSettings && currentUser.Id !== '4f16ca50-b393-47d0-95cc-081e790cc1f6') {
      const state = [...this.toggleableColumns, new TableColumn<SpecLinesEditColumns>(this.showOwnProperties)];
      const value = JSON.stringify(instanceToPlain(state));
      this.resourceService.updateCurrentUserPropertyLineValue(GLOBAL.Properties.SpecLinesEditColumns, value).pipe(takeUntil(this.componentDestroyed)).subscribe(() => {
        this.shouldSaveSettings = false;
        this.resourceService.getCurrentUserSpecs().pipe(takeUntil(this.componentDestroyed)).subscribe(specs => {
          currentUser.Specs = specs
          this.stateService.currentUser$.next(currentUser);
        });
      });
    }
  }

  getColumnStates(user: WebUserModel): TableColumn<SpecLinesEditColumns>[] {
    const columnStateSpec = user.Specs?.find(spec => spec.PropertyId === GLOBAL.Properties.SpecLinesEditColumns);
    if (columnStateSpec) {
      return plainToInstance(TableColumn, JSON.parse(columnStateSpec.Value)) as unknown as TableColumn<SpecLinesEditColumns>[];
    } else {
      return null;
    }
  }

  paste(fromClipboard: boolean = false) {
    this.copyPasteService.paste('Spec', null, null, null, fromClipboard).then(result => {
      if (result?.type === 'Spec') {
        (result.content as SpecLineModel[]).forEach(spec => {
          if (!this.context.Specs.find(value => value.Id === spec.Id)) {
            this.context.Specs.push(spec);
          }
        });
        this.context.Specs = [...this.context.Specs];
        this.changed.emit();
      }
    });
  }

  onModalEvent(event: 'opened' | 'closed', cellId: string) {
    if (event === 'opened') {
      this.editableCellId = cellId;
      const clickListener$ = fromEvent(window, 'click');
      const keydownListener$ = fromEvent(window, 'keydown');
      this.clickSubscription = merge(clickListener$, keydownListener$).pipe(takeUntil(this.componentDestroyed)).subscribe(() => this.ensureEditableCell());
    } else if (event === 'closed') {
      this.editableCellId = undefined;
      this.clickSubscription.unsubscribe();
    }
  }

  private ensureEditableCell() {
    if (!this.editableCellId) return;
    document.getElementById(this.editableCellId)?.click();
  }

  changeSelection(tableCheckBox: TableCheckbox, event: MouseEvent) {
    const isChecked = !!tableCheckBox.checked;
    if (event.button === 0 && event.shiftKey) {
      if (isChecked) {
        this.table.selectRange(event, tableCheckBox.index);
      } else {
        this.table.rangeRowIndex = tableCheckBox.index;
        this.table.clearSelectionRange(event);
        this.table.tableService.onSelectionChange();
      }
    }
    this.table.anchorRowIndex = tableCheckBox.index;
  }

  asLine(line: any) {
    return line as SpecLineModel;
  }
}
